import { SoundController } from "@lib/titan2-slingo/dist/game/controller/SoundController";
import { GameSoundConstants } from "../view/abstract/constant/GameSoundConstants";
import { FreeSpinOverlayEvent, ReelEvent, SlingoEvent } from "@lib/titan2-slingo/dist/game/controller/event/SlingoEvent";
import { FreeSpinReelControllerEvent, FreeSpinsRemainingViewEvent, GameEvent, SoundEvent } from "./event/GameEvent";
import gsap from "gsap";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { FeatureRedFishEvents } from "../view/abstract/display/features/redfish/FeatureRedFish";
import { FeatureType, FreeSpinReelState } from "../model/GameModel";
import { FishFoodType } from "../model/data/ChanceGamesData";
import { GameState } from "@lib/titan2-slingo/dist/api/model/enum/GameState";
import { MainTransitionEvents } from "../view/abstract/display/GameMainTransition";
import { FeatureSelectorControllerEvents } from "./FeatureSelectorController";
/**
 * GameSoundController.ts
 */
export class GameSoundController extends SoundController {
    constructor() {
        super(...arguments);
        this.payLadderSoundsPlaying = {};
        this.tally = {};
    }
    postCreate() {
        super.postCreate();
        //This may resolve ios audio cut-out issues
        document.addEventListener('visibilitychange', () => {
            const { ctx } = Howler;
            if (ctx && !document.hidden) {
                setTimeout(() => {
                    ctx.resume();
                }, 100);
            }
        }, false);
    }
    playSoundId(soundId) {
        return this.playSound(this.getSoundPropertiesFromId(soundId));
    }
    stopSoundId(soundId) {
        this.stopSound(this.getSoundPropertiesFromId(soundId));
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerCore(CommonGameEvent.STAKE_SELECTOR_OPEN, this.handleStakeSelectorOpen);
        this.registerCore(CommonGameEvent.STAKE_SELECTOR_CLOSE, this.handleStakeSelectorClosed);
        this.registerCore(CommonGameEvent.OPTIONS_MENU_OPEN, this.handleButtonClick);
        this.registerCore(CommonGameEvent.OPTIONS_MENU_CLOSE, this.handleButtonClick);
        this.registerGame(CommonGameEvent.PURCHASE_ENTRY, this.handlePurchaseEntry);
        this.registerGame(CommonGameEvent.START_GAME, this.playBackground);
        this.registerGame(SlingoEvent.PAY_TABLE_OPEN, this.handleButtonClick);
        this.registerGame(SlingoEvent.PAY_TABLE_CLOSE, this.handleButtonClick);
        this.registerGame(SlingoEvent.REEL_STRIKE_JOKER, this.handleReelStrikeJoker);
        this.registerGame(SlingoEvent.REEL_STRIKE_SUPER_JOKER, this.handleReelStrikeSuperJoker);
        this.registerGame(SlingoEvent.REEL_STRIKE_FREE_SPIN, this.handleReelStrikeFreeSpin);
        this.registerGame(SlingoEvent.REEL_STRIKE_DEVIL, this.handleReelStrikeDevil);
        this.registerGame(SlingoEvent.REEL_STRIKE_COIN, this.handleReelStrikeCoin);
        this.registerGame(SlingoEvent.DAB_ACTIVE, this.handleDabActive);
        this.registerGame(FreeSpinReelControllerEvent.FREE_SPIN_TRANSITION_IN_START, this.handleFreeSpinTransitionInStart);
        this.registerGame(FreeSpinReelControllerEvent.FREE_SPIN_RETRIGGER_START, this.handleFreeSpinRetriggerStart);
        this.registerGame(FreeSpinReelControllerEvent.FREE_SPIN_TRANSITION_OUT_START, this.handleFreeSpinTransitionOutStart);
        this.registerGame(FreeSpinReelControllerEvent.FREE_SPIN_GAME_OVER_TRANSITION_OUT_START, this.handleFreeSpinTransitionOutStart);
        this.registerGame(SlingoEvent.FREE_SPIN_AWARD, this.handleFreeSpinCollect);
        this.registerGame(FreeSpinOverlayEvent.FREE_SPIN_END, this.handleFreeSpinEnd);
        this.registerGame(GameEvent.COIN_WIN_START, this.handleCoinWinCollect);
        this.registerGame(FreeSpinsRemainingViewEvent.ACTIVATE, this.handleFreeSpinActivate);
        this.registerGame(ReelEvent.REEL_STOPPED, this.handleReelsComplete);
        for (let i = 1; i <= 12; i++) {
            this.registerGame(SlingoEvent.getPatternEvent(i), () => this.handleSlingoPayLadder(i));
        }
        this.registerGame(CommonGameEvent.REPLAY_START, this.handlePurchaseEntryRequest);
        this.registerGame(CommonGameEvent.REPLAY_FINISHED, this.stopBackground);
        this.unregisterGame(SlingoEvent.REELS_COMPLETE);
        this.registerGame(SoundEvent.CLICK, this.playClick);
        this.registerGame(MainTransitionEvents.TRANSITION_INTRO_START, this.stopBackground);
        this.registerGame(FeatureRedFishEvents.DROP_BOWL_IN, this.playBowlSlideIn);
        this.registerGame(FeatureRedFishEvents.FISH_ENTER_BOWL, this.playFishDive);
        this.registerGame(SoundEvent.FISH_KISS, this.playFishKiss);
        this.registerGame(SoundEvent.BIG_WIN_START, this.playBigWin);
        this.registerGame(SoundEvent.BIG_WIN_STOP, this.stopBigWin);
        this.registerGame(SoundEvent.BIG_WIN_THRESHOLD, this.playBigWinThreshold);
        this.registerGame(SlingoEvent.FULL_HOUSE_ANIMATION_PHASE_ONE, this.playFullHouse);
        this.registerGame(FeatureSelectorControllerEvents.PRESENT_FEATURE_SELECTION, this.playFeatureSelector);
    }
    playFullHouse() {
        if (this.background) {
            this.fadeSoundTo(this.backgroundSound, 0.3, GameSoundConstants.musicBlendTime);
        }
        this.backgroundSound = this.playSound(this.getSoundPropertiesFromId("FULL_HOUSE"));
    }
    playFeatureSelector() {
        this.stopBackground();
        this.background = this.getSoundPropertiesFromId("BONUS_PICK");
        this.backgroundSound = this.playSound(this.background);
    }
    handleStakeSelectedSame() {
        this.playSound(this.getSoundPropertiesFromId("SAME_STAKE"));
    }
    handleStakeSelectorOpen() {
        this.playSound(this.getSoundPropertiesFromId("STAKE_SELECTOR_CLICKED"));
    }
    handleStakeSelectorClosed() {
        this.handleStakeSelectorOpen();
    }
    handlePurchaseEntry() {
        this.stopSound(this.getSoundPropertiesFromId("RESPINS_LOOP"));
        if (this.backgroundSound) {
            this.backgroundSound.rate(1);
        }
        this.goldFishRespinMode = false;
        this.playBackground();
    }
    handleButtonClick() {
        this.playSound(this.getSoundPropertiesFromId("NULL_CLICK"));
    }
    handleScatterWinAppear() {
        super.handleScatterWinAppear();
        if (this.backgroundSound) {
            this.fadeSoundTo(this.backgroundSound, 0.1, .5);
        }
    }
    handleScatterWinDisappear() {
        super.handleScatterWinAppear();
        if (this.backgroundSound) {
            this.fadeSoundTo(this.backgroundSound, 1, .5);
        }
    }
    playBackground() {
        let properties = this.getBackground();
        if (properties != this.background) {
            const switchingMusicTracks = this.backgroundSound !== undefined;
            this.stopBackground();
            this.backgroundSound = this.playSound(properties);
            this.backgroundSound.volume(0);
            this.backgroundSound.howl.rate(this.model.getFreeSpinReelState() === FreeSpinReelState.FREE_SPIN_REEL ? 1.12 : 1);
            if (switchingMusicTracks) {
                this.fadeSoundTo(this.backgroundSound, 1, GameSoundConstants.musicBlendTime);
            }
            else {
                this.backgroundSound.volume(1);
            }
            this.background = properties;
        }
    }
    stopBackground() {
        if (this.background) {
            const oldBackgroundProperties = this.background;
            const oldBackgroundSound = this.backgroundSound;
            this.fadeSoundTo(oldBackgroundSound, 0, GameSoundConstants.musicBlendTime).then(() => {
                this.stopSound(oldBackgroundProperties);
            });
            this.background = undefined;
            this.backgroundSound = undefined;
        }
    }
    fadeSoundTo(sound, toVolume, duration) {
        return new Promise(resolve => {
            const volume = { value: sound.volume() };
            return gsap.timeline().to(volume, {
                value: toVolume,
                onUpdate: () => {
                    sound.volume(volume.value);
                },
                onComplete: () => {
                    resolve();
                },
                duration
            });
        });
    }
    getBackground() {
        return GameSoundConstants.getBackground(this.model.getCurrentMatchedPatterns(), this.model.getState() === GameState.PURCHASE_ENTRY);
    }
    handleSlingoPayLadder(matches) {
        const properties = GameSoundConstants.getLevelUp(matches);
        if (this.payLadderSoundsPlaying[properties.name]) {
            return;
        }
        const sound = this.playSound(properties);
        this.payLadderSoundsPlaying[properties.name] = true;
        sound.once("end", () => {
            this.payLadderSoundsPlaying[properties.name] = false;
        });
    }
    handleLevelUp() {
        // noop
    }
    handleLoaderRemove() {
        this.loaderClosed = true;
        const sound = this.playSound(this.getSoundPropertiesFromId("GAME_READY"));
        const isFeatureSelector = this.model.getGameProgress()?.getMatchedPatterns() === 12;
        sound.volume(0.6);
        sound.once("end", () => {
            if (this.gameInProgress && !isFeatureSelector) {
                this.openingJingleSound = undefined;
                this.playBackground();
            }
        });
        this.openingJingleSound = sound;
    }
    handleFreeSpinActivate() {
        this.playSound(this.getSoundPropertiesFromId("FREE_SPIN_RAINBOW"));
    }
    handleDabSuccess() {
        let ticketDab = this.model.getDabData();
        this.playSound(GameSoundConstants.getDab(ticketDab.getNumberOfDabs())).volume(1);
    }
    handleDabActive() {
        if (this.loaderClosed) {
            this.playSound(this.getSoundPropertiesFromId("DAB_ACTIVE")).volume(0.5);
        }
    }
    handleAvailableWin() {
        if (this.loaderClosed) {
            this.playSound(this.getSoundPropertiesFromId("AVAILABLE_WIN")).volume(1);
        }
    }
    handleReelStrike() {
        if (this.model.getFreeSpinReelState() === FreeSpinReelState.FREE_SPIN_REEL) {
            this.playSound(this.getSoundPropertiesFromId("RESPIN_REEL_STOP")).volume(0.75);
        }
        this.playSound(this.getSoundPropertiesFromId("REEL_STRIKE")).volume(0.3);
        const symbol = this.model.getReelDisplayData().getStopSymbol();
        const id = symbol.getId();
        if (!Number.isNaN(parseInt(id))) {
            if (this.idMatchesTicket(id)) {
                this.playSound(this.getSoundPropertiesFromId("REEL_STRIKE_NUMBER_MATCH")).volume(0.5);
            }
        }
    }
    handleReelStrikeJoker() {
        this.playSound(this.getSoundPropertiesFromId("REEL_STRIKE_JOKER")).volume(0.5);
    }
    handleReelStrikeSuperJoker() {
        this.playSound(this.getSoundPropertiesFromId("REEL_STRIKE_SUPER_JOKER")).volume(0.5);
    }
    handleReelStrikeFreeSpin() {
        this.playSound(this.getSoundPropertiesFromId("REEL_STRIKE_FREE_SPIN")).volume(0.5);
    }
    handleReelStrikeCoin() {
        this.playSound(this.getSoundPropertiesFromId("REEL_STRIKE_COIN")).volume(0.5);
    }
    handleReelStrikeDevil() {
        this.playSound(this.getSoundPropertiesFromId("REEL_STRIKE_DEVIL")).volume(0.5);
    }
    handleFreeSpinRetriggerStart() {
        this.firstFreeSpinUpdate = true;
    }
    handleFreeSpinTransitionInStart() {
        this.firstFreeSpinUpdate = true;
        this.playSound(this.getSoundPropertiesFromId("FREE_SPIN_TRANSITION_START"))?.volume(1);
        this.playSound(this.getSoundPropertiesFromId("RESPINS_LOOP"))?.volume(0.35);
        if (this.backgroundSound) { // if we resumed, the background audio might not have loaded yet.
            const howl = this.backgroundSound.howl;
            gsap.to(howl, {
                rate: 1.122,
                duration: 0.5
            });
        }
    }
    handleFreeSpinTransitionOutStart() {
        this.playSound(this.getSoundPropertiesFromId("FREE_SPIN_TRANSITION_START"))?.volume(1);
        this.stopSound(this.getSoundPropertiesFromId("RESPINS_LOOP"));
        if (this.backgroundSound) { // if we resumed, the background audio might not have loaded yet.
            const howl = this.backgroundSound.howl;
            gsap.to(howl, {
                rate: 1,
                duration: 0.5
            });
        }
    }
    handleFreeSpinCollect() {
        this.playSound(this.getSoundPropertiesFromId("FREE_SPIN_COLLECT")).volume(1);
    }
    handleFreeSpinEnd() {
        if (this.firstFreeSpinUpdate) {
            this.playSound(this.getSoundPropertiesFromId("FREE_SPIN_FIRST_UPDATE"))?.volume(1);
        }
        else {
            this.firstFreeSpinUpdate = false;
            this.playSound(this.getSoundPropertiesFromId("FREE_SPIN_UPDATE"))?.volume(1);
        }
    }
    handleCoinWinCollect() {
        this.playSound(this.getSoundPropertiesFromId("COIN_WIN_COLLECT")).volume(0.6);
    }
    playReelLoop() {
        if (this.model.getFreeSpinReelState() === FreeSpinReelState.FREE_SPIN_REEL) {
            this.playSound(this.getSoundPropertiesFromId("RESPIN_START")).volume(0.75);
        }
        this.stopSound(this.getSoundPropertiesFromId("REEL_LOOP"));
        this.playSound(this.getSoundPropertiesFromId("REEL_LOOP")).volume(1);
    }
    handleSuspenseSpin() {
        if (!this.suspenseSpinSound) {
            this.suspenseSpinSound = this.playSound(this.getSoundPropertiesFromId("SUSPENSE_SPIN"));
        }
    }
    handleReelsComplete() {
        const stopIndex = this.model.getReelDisplayData().getStopIndex();
        if (stopIndex < 4) {
            return;
        }
        this.suspenseSpinSound = undefined;
        this.stopSound(this.getSoundPropertiesFromId("SUSPENSE_SPIN"));
        this.stopSound(this.getSoundPropertiesFromId("REEL_LOOP"));
    }
    idMatchesTicket(id) {
        const ticketDab = this.model.getDabData();
        const autoHighlightCells = ticketDab.getAutoHighlightCells();
        return autoHighlightCells.findIndex(cell => cell.getNumber() === parseInt(id)) > -1;
    }
    handleGameSummary() {
        this.stopBackground();
        this.stopBonusMusic();
        let result = this.model.getResultData();
        if (result.hasNoWin()) {
            this.playSound(this.getSoundPropertiesFromId("GAME_OVER"));
        }
        else {
            this.playSound(this.getSoundPropertiesFromId("RESULT_PANEL")).volume(0.5);
        }
    }
    stopBonusMusic() {
        this.stopGreenFishMusic();
        this.stopRedFishMusic();
        this.stopPurpleFishMusic();
        this.stopFishFoodMusic();
        this.stopGoldFishMusic();
    }
    playGreenFishMusic() {
        this.stopBackground();
        const intro = this.playSoundId("SALSA_INTRO");
        intro.once("end", () => {
            this.playSoundId("SALSA_LOOP").loop(true);
        });
    }
    stopGreenFishMusic() {
        this.stopSoundId("SALSA_LOOP");
    }
    playRedFishMusic() {
        this.stopBackground();
        this.playSoundId("REDPURPLE_INTRO");
        gsap.delayedCall(2, () => {
            this.playSoundId("REDPURPLE_MUSIC").loop(true);
        });
    }
    stopRedFishMusic() {
        this.stopSoundId("REDPURPLE_MUSIC");
    }
    playPurpleFishMusic() {
        this.stopBackground();
        const intro = this.playSoundId("PURPLEFISH_INTRO");
        intro.once("end", () => {
            this.playSoundId("PURPLEFISH_MUSIC").loop(true);
        });
    }
    stopPurpleFishMusic() {
        this.stopSoundId("PURPLEFISH_MUSIC");
    }
    playGoldFishMusic() {
        this.stopBackground();
        const intro = this.playSoundId("GOLDFISH_MUSIC_INTRO");
        intro.once("end", () => {
            this.goldFishMusic = this.playSoundId("GOLDFISH_MUSIC_LOOP");
            this.goldFishMusic.loop(true);
        });
    }
    stopGoldFishMusic() {
        this.stopSoundId("GOLDFISH_MUSIC_LOOP");
        this.goldFishMusic = undefined;
    }
    playFishFoodMusic() {
        this.stopBackground();
        const intro = this.playSoundId("FISHFOOD_INTRO");
        intro.once("end", () => {
            this.playSoundId("FISHFOOD_MUSIC").loop(true);
        });
    }
    stopFishFoodMusic() {
        this.stopSoundId("FISHFOOD_MUSIC");
    }
    playGreenFishCollect() {
        this.playSoundId("RS_ANGELFISH").volume(1);
    }
    playRedFishCollect() {
        this.playSoundId("RS_CLOWN").volume(1);
    }
    playPurpleFishCollect() {
        this.playSoundId("RS_GOLDFISH").volume(1);
    }
    playGoldFishCollect() {
        this.playSoundId("RS_DISCUS").volume(1);
    }
    playFishFoodCollect() {
        this.playSoundId("FISHFOOD_EXPLOSION").volume(1);
    }
    playBowlSlideIn() {
        this.playSoundId("BOWL_SLIDEIN").volume(1);
    }
    playFishSwim() {
        this.playSoundId("FISH_SWIM").volume(1);
    }
    playFishDive() {
        this.playSoundId("FISH_DIVE").volume(1);
    }
    playSalsaIntro() {
        this.playSoundId("SALSA_INTRO").volume(1);
    }
    playSalsaLoop() {
        this.playSoundId("SALSA_LOOP").volume(1);
    }
    playBubbleAppear(event) {
        const payload = event.getPayload();
        let count = payload.count;
        count = (count % 3) + 1;
        this.playSoundId(`BUBBLE_APPEAR${count}`).volume(1);
    }
    playBubblePop() {
        this.playSoundId("BUBBLE_POP").volume(1);
    }
    playBubbleStretchPop(event) {
        const payload = event.getPayload();
        let count = payload.count;
        count = (count % 3) + 1;
        this.playSoundId(`STRETCH_POP${count}`).volume(1);
    }
    playBubbleDisappear() {
        this.playSoundId("BUBBLE_DISAPPEAR").volume(0.5);
    }
    playFishKiss() {
        this.playSoundId("FISH_KISS").volume(1);
    }
    playBubbleRiseLong() {
        this.playSoundId("BUBBLE_RISE_LONG").volume(1);
    }
    playBubbleRise() {
        this.playSoundId("BUBBLE_RISE1").volume(1);
    }
    playCoinDrop() {
        this.playSoundId("COINDROP").volume(1);
    }
    playClick() {
        this.playSoundId("CLICK").volume(1);
    }
    playCollectBonus() {
        this.stopBackground();
        // this.playSoundId("BONUS_BRASS").volume(1);
        const featureType = this.model.featureTypeFromMatches(this.model.getCurrentMatchedPatterns());
        switch (featureType) {
            case FeatureType.GREEN_FISH:
                this.playGreenFishCollect();
                break;
            case FeatureType.RED_FISH:
                this.playRedFishCollect();
                break;
            case FeatureType.PURPLE_FISH:
                this.playPurpleFishCollect();
                break;
            case FeatureType.FISH_FOOD:
                this.playFishFoodCollect();
                break;
            case FeatureType.GOLD_FISH:
                this.playGoldFishCollect();
                break;
        }
    }
    playFishFoodTurtlePickSelected(ev) {
        this.playSoundId("TURTLE_SPIN").volume(1);
    }
    playFishFoodShowTurtles(ev) {
        this.playSoundId("PICKATURTLE").volume(1);
    }
    playFishFoodRevealTurtlePick(ev) {
        this.stopSoundId("TURTLE_SPIN");
        this.playSoundId("TURTLE_POOPER_PICK").volume(1);
    }
    playFishFoodRevealPick(ev) {
        const payload = ev.getPayload();
        const { count, type } = payload;
        if (type === FishFoodType.Turtle) {
            this.playSoundId("TURTLE_TALLY").volume(1);
        }
        else if (count < 3) {
            this.playSoundId("FISHFOOD_TRIG1").volume(1);
        }
        else if (count === 3) {
            this.playSoundId("FISHFOOD_TRIG2").volume(1);
        }
        else if (count === 4) {
            this.playSoundId("FISHFOOD_TRIG3").volume(1);
        }
    }
    playFishFoodPickSelected() {
        this.playSoundId("FISHFOODPICK").volume(1);
    }
    playTallyGreenFish() {
        this.tally.green ? this.tally.green++ : this.tally.green = 1;
        this.playSoundId(`GREEN_FISH_TALLY_${Math.min(this.tally.green, 4)}`).volume(1);
    }
    playTallyRedFish() {
        this.tally.red ? this.tally.red++ : this.tally.red = 1;
        this.playSoundId(`CLOWN_FISH_TALLY_${Math.min(this.tally.red, 4)}`).volume(1);
    }
    playTallyPurpleFish() {
        this.tally.purple ? this.tally.purple++ : this.tally.purple = 1;
        this.playSoundId(`PURPLE_FISH_TALLY_${Math.min(this.tally.purple, 4)}`).volume(1);
    }
    playTallyGoldFish() {
        this.tally.gold ? this.tally.gold++ : this.tally.gold = 1;
        this.playSoundId(`YELLOW_FISH_TALLY_${Math.min(this.tally.gold, 4)}`).volume(1);
    }
    playTallyBlueFish() {
        this.tally.blue ? this.tally.blue++ : this.tally.blue = 1; //TODO is this needed
        this.playSoundId(`BLUE_FISH_TALLY`).volume(1);
    }
    resetTally() {
        this.tally = {};
    }
    handleGoldfishRespinTransitionTo() {
        this.goldFishRespinMode = true;
        this.playSound(this.getSoundPropertiesFromId("BUBBLE_RISE_LONG")).volume(1);
        this.playSound(this.getSoundPropertiesFromId("FREE_SPIN_TRANSITION_START")).volume(1);
        this.playSound(this.getSoundPropertiesFromId("RESPINS_LOOP")).volume(0.35);
        this.getSoundPropertiesFromId("GOLDFISH_MUSIC_LOOP");
        const howl = this.goldFishMusic.howl;
        gsap.to(howl, {
            rate: 1.122,
            duration: 0.5
        });
    }
    handleGoldfishRespinTransitionFrom() {
        this.goldFishRespinMode = false;
        this.playSound(this.getSoundPropertiesFromId("FREE_SPIN_TRANSITION_START")).volume(1);
        this.stopSound(this.getSoundPropertiesFromId("RESPINS_LOOP"));
        const howl = this.goldFishMusic.howl;
        gsap.to(howl, {
            rate: 1,
            duration: 0.5
        });
    }
    handleGoldFishSpin() {
        if (this.goldFishRespinMode) {
            this.playSound(this.getSoundPropertiesFromId("RESPIN_START")).volume(0.75);
        }
    }
    handleGoldFishReelStop() {
        if (this.goldFishRespinMode) {
            this.playSound(this.getSoundPropertiesFromId("RESPIN_REEL_STOP")).volume(0.75);
        }
    }
    handleGoldfishUpdateSpinCount() {
        this.resetTally();
    }
    playGoldFishMultiplier(ev) {
        const payload = ev.getPayload();
        const { multiplier } = payload;
        if (multiplier <= 2)
            this.playSoundId("MULTIPLIER_2X").volume(1);
        else if (multiplier <= 3)
            this.playSoundId("MULTIPLIER_3X").volume(1);
        else if (multiplier <= 5)
            this.playSoundId("MULTIPLIER_5X").volume(1);
        else if (multiplier <= 7)
            this.playSoundId("MULTIPLIER_7X").volume(1);
        else
            this.playSoundId("MULTIPLIER_10X").volume(1);
    }
    playBangupBlueFish() {
        this.playSoundId("BLUE_BANGUP").loop(true);
    }
    stopBangupBlueFish() {
        this.stopSoundId("BLUE_BANGUP");
        this.playSoundId("BLUE_BANGUP_END").volume(1);
    }
    playBangupGoldFish() {
        this.playSoundId("GOLDFISH_BANGUP").loop(true);
    }
    stopBangupGoldFish() {
        this.stopSoundId("BIGWIN_LOOP");
        this.playSoundId("BIGWIN_END").volume(1);
    }
    playBigWin() {
        this.playSoundId("BIGWIN_LOOP").loop(true);
    }
    stopBigWin() {
        this.stopSoundId("BIGWIN_LOOP");
        this.playSoundId("BIGWIN_END").volume(1);
    }
    playBigWinThreshold() {
        // this.playSoundId("PICKATURTLE");
    }
    playSpinsLeft() {
        this.playSoundId("SPINS_LEFT");
    }
    playLockReels() {
        this.playSoundId("LOCK");
    }
    playGreenFishWhirlPoolAppear() {
        this.playSoundId("WHIRLPOOL_LOOP");
    }
    playWhirlPoolCollectSting(event) {
        const index = event.getPayload().index;
        this.playSoundId(`WHIRLPOOL_COLLECT_${index + 1}`).volume(1);
        if (index === 7) {
            this.stopSoundId("WHIRLPOOL_LOOP");
        }
    }
}
//# sourceMappingURL=GameSoundController.js.map